<script lang="ts">
  import { push } from 'svelte-spa-router';
  import { loginUser } from '../../../../components/auth/LoginUser';
  import type { BundleOptionHikari01InheritedResult as FetchResult } from '../../../../repositories/bundleoptionhikari01inherited';
  import RepositoryFactory, { BUNDLE_OPTION_HIKARI01_INHERITED } from '../../../../repositories/RepositoryFactory';
  import TerminateQitMailDetail from './TerminateQitMailDetail.svelte';
  import TerminateSpaaqsMailDetail from './TerminateSpaaqsMailDetail.svelte';

  export let props!: FetchResult;
  export let transitionSource!: 'QIT' | 'SPAAQS';

  let pageStatus: 'INPUT' | 'COMPLETE' | 'BACK_QIT_MAIL' | 'BACK_SPAAQS_MAIL' = 'INPUT';

  let currentMailPassword = props.inheritedMailPassword;
  let changedMailPassword: string = '';
  let errorMessage: string = '';

  // 文字列長さチェック 8文字以上 16文字以内
  const isValidateCharactersLength = (value: string) => {
    const len = value.length;
    return len >= 8 && len <= 16;
  };

  // 利用可能文字チェック 半角英数字または半角ハイフン(-)
  const isAvailableCharacters = (value: string) => {
    const regexp = new RegExp(/[a-zA-Z0-9-]+$/);
    return regexp.test(value);
  };

  // 文字種類制約チェック 英字数字を両方含める
  const isConstraintCharacters = (value: string) => {
    const regexp = new RegExp(/^(?=.*?[a-z])(?=.*?\d)[a-z\d]{8,16}$/i);
    return regexp.test(value);
  };

  const handleTransitionComplete = (): void => {
    errorMessage = '';

    if (!changedMailPassword) {
      errorMessage = '変更後のパスワードを入力してください';
      return;
    }
    if (!isValidateCharactersLength(changedMailPassword)) {
      errorMessage = '8文字以上16文字以内で指定してください';
      return;
    }
    if (!isAvailableCharacters(changedMailPassword)) {
      errorMessage = '利用できない文字が入力されています';
      return;
    }
    if (!isConstraintCharacters(changedMailPassword)) {
      errorMessage = '英字数字を両方含めてください';
      return;
    }

    const repository = RepositoryFactory[BUNDLE_OPTION_HIKARI01_INHERITED];
    repository
      .changeMailPassword({
        bundleOptionHikari01InheritedId: props.bundleOptionHikari01InheritedId,
        inheritedMailPassword: changedMailPassword
      })
      .then((res) => {
        pageStatus = 'COMPLETE';
      })
      .catch((error) => {
        if (error.response.status === 403) {
          alert('セッションが切れました。再度ログインしてください。');
          loginUser.logout();
          push('/login');
        } else {
          alert('エラーが発生しました。');
        }
      });
  };
  const handleTransitionBack = (): void => {
    if (transitionSource === 'QIT') {
      pageStatus = 'BACK_QIT_MAIL';
    } else {
      pageStatus = 'BACK_SPAAQS_MAIL';
    }
  };
</script>

{#if pageStatus === 'INPUT'}<!-- 入力画面 -->
  <div class="h-auto p-6 bg-white m-auto max-w-4xl">
    <div class="mb-6">
      <div class="container flex justify-between">
        <h2 class="text-xl font-bold text-gray-700">メールパスワード変更</h2>
      </div>
    </div>
    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-10">
        <div class="mb:font-sm w-full">
          <ul class="list-none mt-4">
            <li>
              <p>変更後パスワードを入力の上、変更ボタンを押下ください</p>
            </li>
            <li>
              <p>パスワード変更が反映されるまでに1,2日程度かかります。</p>
            </li>
            <li>
              <p>メールソフト/アプリの設定変更は、上記時間経過後に行ってください。</p>
            </li>

            <div class="flex-1 my-6">
              <form>
                <div class="flex flex-col justify-items-center items-center mx-auto">
                  <div class="shadow rounded bg-gray-50 w-11/12 content-center p-4">
                    <div class="inline-grid grid-cols-2 items-center min-w-full p-2">
                      <div class="grid-1 justify-self-start w-full">
                        <span class="label-title">現在のパスワード</span>
                      </div>
                      <div class="grid-1 justify-self-start w-full">
                        <p class="label-value">{currentMailPassword}</p>
                      </div>
                    </div>

                    <div class="inline-grid grid-cols-2 items-center min-w-full p-2">
                      <div class="grid-1 justify-self-start w-full">
                        <span class="label-title">変更後のパスワード</span>
                      </div>
                      <div class="grid-1 justify-self-start w-full">
                        <input type="text" maxlength="16" bind:value={changedMailPassword} />
                        {#if errorMessage}
                          <p class="text-red-500 text-xs mt-2">
                            {errorMessage}
                          </p>
                        {/if}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </ul>
        </div>
        <div class="flex flex-col justify-items-center items-center mx-auto">
          <div class="shadow rounded bg-red-50 w-full content-center p-1">
            <div class="flex-1 m-2">
              <div class="inline-flex justify-items-center items-center min-w-full">
                <div class="text-sm w-full">
                  <ul class="list-none">
                    <li>
                      <p>利用可能文字：半角英数字または半角ハイフン(-)</p>
                    </li>
                    <li>
                      <p>文字数：8文字以上16文字以内</p>
                    </li>
                    <li>
                      <p>文字種類制約：英字数字を両方含めてください</p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col justify-items-center items-center">
          <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
            <button class="button-secondary md:ml-5 mb-5 w-32" on:click={handleTransitionBack}> 戻る </button>
            <button class="button-primary md:ml-5 mb-5 w-32" on:click={handleTransitionComplete}> 変更 </button>
          </div>
        </div>
      </div>
    </div>
  </div>
{:else if pageStatus === 'BACK_QIT_MAIL'}<!-- 戻る Qitメール画面 -->
  <TerminateQitMailDetail {props} />
{:else if pageStatus === 'BACK_SPAAQS_MAIL'}<!-- 戻る Spaaqsメール画面 -->
  <TerminateSpaaqsMailDetail {props} />
{:else if pageStatus === 'COMPLETE'}<!-- 完了画面 -->
  <div class="h-auto p-6 bg-white m-auto max-w-4xl">
    <div class="mb-6">
      <div class="container flex justify-between">
        <h2 class="text-xl font-bold text-gray-700">メールパスワード変更</h2>
      </div>
    </div>

    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-10">
        <div class="inline-flex justify-items-center items-center min-w-full mb-20">
          <div class="mb:font-sm w-full">
            <ul class="list-none mt-4">
              <li>
                <p>パスワード変更を受け付けました</p>
              </li>
              <li>
                <p>パスワード変更が反映されるまでに1,2日程度かかります。</p>
              </li>
              <li>
                <p>メールソフト/アプリの設定変更は、上記時間経過後に行ってください。</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}

<style lang="postcss">
  p:not([class]) {
    @apply tracking-normal mb-1;
  }
  .label-title {
    @apply text-sm font-medium text-gray-700;
  }
  .label-value {
    @apply text-sm font-medium text-gray-900;
  }
  input[type='text'] {
    @apply rounded border py-2 px-3 text-sm placeholder-gray-400 focus:placeholder-gray-200 focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-transparent w-full;
  }
  .button-primary {
    @apply bg-green-400 rounded hover:bg-green-500 text-sm font-semibold text-white focus:outline-none px-10 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default;
  }

  .button-secondary {
    @apply bg-gray-300 rounded hover:bg-gray-400 text-gray-500 text-sm font-semibold focus:outline-none px-10 py-2;
  }
</style>
